import React, { useState, useEffect, useCallback, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { logAuthEvent, addBreadcrumb, logError } from "@/lib/logger";

interface SessionUser {
  id: string;
  email?: string;
  [key: string]: any;
}

interface SessionContextType {
  user: SessionUser | null;
  session: any;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isLoading: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Helper: Create anonymous session if needed
  const ensureSession = useCallback(async () => {
    addBreadcrumb('Initializing session', {}, 'info', 'auth');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      addBreadcrumb('No session found, creating anonymous session', {}, 'info', 'auth');
      const { data: { session: anonSession }, error } = await supabase.auth.signInAnonymously();
      
      if (error || !anonSession) {
        throw new Error(`Failed to create anonymous session: ${error?.message || 'Unknown error'}`);
      }
      
      setSession(anonSession);
      setUser(anonSession.user);
      setSentryUser(anonSession.user.id, anonSession.user.email);
      logAuthEvent('anonymous', anonSession.user.id, undefined, { method: 'auto_create' });
    } else {
      setSession(session);
      setUser(session.user);
      setSentryUser(session.user.id, session.user.email);
      logAuthEvent('login', session.user.id, undefined, { method: 'existing_session' });
    }
    setIsLoading(false);
    addBreadcrumb('Session initialized', { user_id: session?.user?.id }, 'info', 'auth');
  }, []);

  useEffect(() => {
    ensureSession().catch((error) => {
      // Log the error to Sentry
      logError(error instanceof Error ? error : new Error(String(error)), {
        context: 'session_initialization',
      });
      
      // Don't redirect - let AuthRequired component handle that
      // Just set loading to false so the auth check can happen
      setIsLoading(false);
    });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      addBreadcrumb(`Auth state changed: ${event}`, { has_session: !!session }, 'info', 'auth');
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setSentryUser(session.user.id, session.user.email);
        const authEvent = event === 'INITIAL_SESSION' ? 'login' : event as 'login' | 'logout' | 'signup' | 'error' | 'anonymous';
        logAuthEvent(authEvent, session.user.id);
      } else {
        clearSentryUser();
        logAuthEvent('logout');
      }
    });
    return () => subscription.unsubscribe();
  }, [ensureSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await ensureSession();
    setIsLoading(false);
  }, [ensureSession]);

  const signUp = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    const { error } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: window.location.origin + "/" } });
    if (error) throw error;
    await ensureSession();
    setIsLoading(false);
  }, [ensureSession]);

  const signOut = useCallback(async () => {
    setIsLoading(true);
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    clearSentryUser();
    logAuthEvent('logout');
    setIsLoading(false);
  }, []);

  return (
    <SessionContext.Provider value={{ user, session, signIn, signUp, signOut, isLoading }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used within a SessionProvider");
  return context;
}
